import { Button, HStack, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  UtilizationReportDataSource,
  UtilizationReportOrderByColumn,
  type UtilizationReportQuery,
} from "@src/__generated__/urql-graphql";
import type { UtilizationReportStore } from "@src/components/modules/reports/utilization/stores/utilization-report";
import {
  AvailableCapacityTooltip,
  TrackedAndAllocatedTooltip,
  userCell,
} from "@src/components/modules/reports/utilization/UtilizationReport";
import { ACTIONS_COLUMN_ID, type BodyColumnDef } from "@src/components/ui-kit";
import { RangeProgressBar } from "@src/components/ui-kit/ProgressBar/RangeProgressBar";
import { currency } from "@src/utils/formatters";
import { formatSecondsToHours } from "@src/utils/time";
import { differenceInMonths } from "date-fns";
import { computed } from "mobx";
import { now } from "mobx-utils";
import { useMemo } from "react";

export type UtilizationTableRow = {
  id: string;
  user: UtilizationReportQuery["utilizationReport"]["data"][0]["user"];
  report: UtilizationReportQuery["utilizationReport"]["data"][0]["report"];
};

export function useUtilizationTableCols(store: UtilizationReportStore) {
  const cols = useMemo<
    | BodyColumnDef<UtilizationTableRow, UtilizationReportOrderByColumn>[]
    | undefined
  >(() => {
    if (store.groupBy) return undefined;

    const isDateInPast = computed(() => {
      return differenceInMonths(store.dateRange.start, now()) < 0;
    }).get();

    let sourceCol: BodyColumnDef<
      UtilizationTableRow,
      UtilizationReportOrderByColumn
    >;

    const billableCol: BodyColumnDef<
      UtilizationTableRow,
      UtilizationReportOrderByColumn
    > = {
      id: "billable",
      meta: { label: t`Billable` },
      header: () => t`Billable`,
    };

    switch (store.dataSource) {
      case UtilizationReportDataSource.TimeTracking:
        sourceCol = {
          id: "tracked",
          meta: { label: t`Tracked` },
          header: () => t`Tracked`,
          orderBy: UtilizationReportOrderByColumn.TrackedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.tracked_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.orderBy =
          UtilizationReportOrderByColumn.TrackedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.tracked_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.Planning:
        sourceCol = {
          id: "allocated",
          meta: { label: t`Allocated` },
          header: () => t`Allocated`,
          orderBy: UtilizationReportOrderByColumn.AllocatedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.allocated_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.orderBy =
          UtilizationReportOrderByColumn.AllocatedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.allocated_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.TaskPositions:
        sourceCol = {
          id: "task-budgeted",
          meta: { label: t`Task budgeted` },
          header: () => t`Task budgeted`,
          orderBy: UtilizationReportOrderByColumn.BudgetedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.budgeted_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.orderBy =
          UtilizationReportOrderByColumn.BudgetedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.budgeted_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.Combined:
        sourceCol = {
          id: "tracked+allocated",
          meta: { label: t`Tracked + Allocated` },
          header: () => t`Tracked + Allocated`,
          orderBy: UtilizationReportOrderByColumn.CombinedTiMe,
          cell: ({ row }) => (
            <TrackedAndAllocatedTooltip
              row={row.original}
              date={store.dateRange.start}
            >
              <span>
                {formatSecondsToHours(
                  row.original.report.combined_time ?? 0,
                  1,
                )}
              </span>
            </TrackedAndAllocatedTooltip>
          ),
        };
        billableCol.orderBy =
          UtilizationReportOrderByColumn.CombinedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.combined_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      default:
        sourceCol = {
          id: "tracked",
          meta: { label: t`Tracked` },
          header: () => t`Tracked`,
          orderBy: UtilizationReportOrderByColumn.TrackedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.tracked_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.orderBy =
          UtilizationReportOrderByColumn.TrackedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.tracked_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
    }

    return [
      {
        id: "name",
        meta: { label: t`Name` },
        header: () => t`Name`,
        enableHiding: false,
        cell: ({ row }) =>
          userCell(row.original.user, () =>
            store.userModalStore.modalState.onOpen({
              id: row.original.user.id,
              dataSource: store.dataSource,
              dateRange: store.dateRange,
            }),
          ),
      },
      {
        id: "team",
        meta: { label: t`Team` },
        header: () => t`Team`,
        orderBy: UtilizationReportOrderByColumn.TeamName,
        cell: ({ row }) => <span>{row.original.user.team?.name}</span>,
      },
      {
        id: "position",
        meta: { label: t`Position` },
        header: () => t`Position`,
        orderBy: UtilizationReportOrderByColumn.WorkTypeTitle,
        cell: ({ row }) => (
          <span>{row.original.user.profile?.defaultWorktype.title}</span>
        ),
      },
      {
        id: "available-capacity",
        meta: { label: t`Available Capacity` },
        header: () => t`Available Capacity`,
        orderBy: UtilizationReportOrderByColumn.AvailableTime,
        cell: ({ row }) => (
          <AvailableCapacityTooltip row={row.original}>
            <span>
              {formatSecondsToHours(row.original.report.available_time, 1)}
            </span>
          </AvailableCapacityTooltip>
        ),
      },
      sourceCol,
      {
        id: "tracked-time",
        meta: { label: t`Tracked time` },
        header: () => t`Tracked time`,
        cell: ({ row }) =>
          formatSecondsToHours(row.original.report.tracked_time ?? 0, 1),
      },
      {
        id: "allocated-time",
        meta: { label: t`Allocated time` },
        header: () => t`Allocated time`,
        cell: ({ row }) =>
          formatSecondsToHours(row.original.report.allocated_time ?? 0, 1),
      },
      {
        id: "left-to-allocate",
        meta: { label: isDateInPast ? t`Unused hours` : t`Left to allocate` },
        header: () => (isDateInPast ? t`Unused hours` : t`Left to allocate`),
        cell: ({ row }) =>
          row.original.report.remaining_time > 0 ? (
            <span>
              {formatSecondsToHours(row.original.report.remaining_time, 1)}
            </span>
          ) : null,
      },
      {
        id: "overtime",
        meta: { label: t`Overtime` },
        header: () => t`Overtime`,
        cell: ({ row }) =>
          row.original.report.overtime > 0 ? (
            <span>{formatSecondsToHours(row.original.report.overtime, 1)}</span>
          ) : null,
      },
      {
        id: "revenue-estimate",
        meta: { label: t`Revenue estimate` },
        header: () => t`Revenue estimate`,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            store.dataSource === UtilizationReportDataSource.Combined
              ? (row.original.report.combined_amount_billable ?? 0)
              : (row.original.report.budgeted_amount_billable ?? 0),
          ),
      },
      billableCol,
      {
        id: "utilization",
        meta: { label: t`Utilization` },
        header: () => t`Utilization`,
        orderBy: UtilizationReportOrderByColumn.UtilizationPercentage,
        cell: ({ row }) => (
          <HStack>
            <RangeProgressBar
              w="96px"
              progress={row.original.report.utilization_percentage ?? 0}
              exceededThreshold={{
                below: row.original.user.profile?.utilization_range_from ?? 0,
                above: row.original.user.profile?.utilization_range_to ?? 0,
              }}
            />
            <Text minW="10" textAlign="end">
              {row.original.report.utilization_percentage ?? 0}%
            </Text>
          </HStack>
        ),
      },
      {
        id: "billable-utilization",
        meta: { label: t`Billable utilization` },
        header: () => t`Billable utilization`,
        orderBy: UtilizationReportOrderByColumn.UtilizationPercentageBillable,
        cell: ({ row }) => (
          <HStack>
            <RangeProgressBar
              w="96px"
              progress={
                row.original.report.utilization_percentage_billable ?? 0
              }
              exceededThreshold={{
                below: row.original.user.profile?.utilization_range_from ?? 0,
                above: row.original.user.profile?.utilization_range_to ?? 0,
              }}
            />
            <Text minW="10" textAlign="end">
              {row.original.report.utilization_percentage_billable ?? 0}%
            </Text>
          </HStack>
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => (
          <Button
            fontWeight="medium"
            onClick={() => {
              store.userModalStore.modalState.onOpen({
                id: row.original.user.id,
                dataSource: store.dataSource,
                dateRange: store.dateRange,
              });
            }}
            role="link"
            size="xs"
            variant="link"
          >
            <Trans>Detail</Trans>
          </Button>
        ),
      },
    ];
  }, [
    store.dataSource,
    store.dateRange,
    store.groupBy,
    store.userModalStore.modalState,
  ]);

  return cols;
}
